import { useRouterState } from '@tanstack/react-router';

import type { OwnRouteParams } from './use_own_route_params';

/**
 * The state.routeParams shape read off the *leaf* match rather than the
 * caller's own match.
 *
 * Chrome rendered by the private shell — the panels container, the panels
 * themselves, the uploader — sits above the page in the tree, so its nearest
 * match is the pathless layout route, which carries no v5Pattern. Such a
 * component must follow the leaf to know which page it is decorating.
 *
 * Still safe against the transition window that useOwnRouteParams guards:
 * state.matches only advances when the router commits, and route and params
 * are taken from the same match, so they can never describe two different
 * pages the way state.routeParams (published synchronously on the history
 * event) combined with a match-keyed id could.
 *
 * Page components should use useOwnRouteParams instead: being frozen to their
 * own match is what stops an outgoing page from observing the incoming route.
 */
export function useLeafRouteParams(): OwnRouteParams {
  return useRouterState({
    select: (state): OwnRouteParams => {
      const match = state.matches[state.matches.length - 1];
      const query: Record<string, string | undefined> = match?.search ?? {};
      return {
        route: match?.staticData?.v5Pattern ?? '',
        params: match?.params ?? {},
        query,
      };
    },
  });
}
